import { ReactElement, Ref, forwardRef, useMemo } from 'react';
import { Table, TableBody, TableFooter, TableHead, TableRow } from '@mui/material';
import { TableComponents, TableVirtuoso, TableVirtuosoHandle, TableVirtuosoProps } from 'react-virtuoso';

export interface PoolVirtualTableProps<T, C> extends Omit<TableVirtuosoProps<T, C>, 'itemContent' | 'components'> {
    tableComponents?: TableComponents<T, C>;
    rowRenderer: (item: T, index: number) => React.ReactNode;
    ref?: Ref<TableVirtuosoHandle>;
}

export const PoolVirtualTable = <T, C = unknown>(props: PoolVirtualTableProps<T, C>): ReactElement => {
    const { rowRenderer, tableComponents, ref, ...baseProps } = props;

    const virtuosoTableComponents: TableComponents<T, C> = useMemo(
        () =>
            tableComponents ?? {
                Table: props => (
                    <Table
                        {...props}
                        sx={{ borderCollapse: 'separate', tableLayout: 'fixed', width: 'unset', minWidth: '100%' }}
                    />
                ),
                TableHead: forwardRef<HTMLTableSectionElement>((props, ref) => <TableHead {...props} ref={ref} />),
                TableRow: TableRow,
                TableBody: forwardRef<HTMLTableSectionElement>((props, ref) => <TableBody {...props} ref={ref} />),
                TableFoot: forwardRef<HTMLTableSectionElement>((props, ref) => <TableFooter {...props} ref={ref} />),
            },
        [tableComponents]
    );

    return (
        <TableVirtuoso
            {...baseProps}
            ref={ref}
            components={virtuosoTableComponents}
            itemContent={(index, item) => rowRenderer(item, index)}
        />
    );
};
